import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import CustomerModal from './CustomerModal';
import ContactModal from './ContactModal';
import SiteModal from './SiteModal';
import JobModal from './JobModal';
import JobCompletedDialog from './JobCompletedDialog';
import { customerService } from '../services/customerService';
import { contactService } from '../services/contactService';
import { siteService } from '../services/siteService';
import { jobService } from '../services/jobService';

const ViewCustomer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [siteDeleteConfirm, setSiteDeleteConfirm] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [contactDeleteConfirm, setContactDeleteConfirm] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobDeleteConfirm, setJobDeleteConfirm] = useState(null);
  const [completedJobDialog, setCompletedJobDialog] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchCustomer = useCallback(async () => {
    try {
      setLoading(true);
      const res = await customerService.getCustomer(id);
      setCustomer(res.data);
      setError('');
    } catch (err) {
      setError('Failed to load customer.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleEditCustomer = async (formData) => {
    await customerService.updateCustomer(id, formData);
    await fetchCustomer();
    setShowEditModal(false);
    showSuccess('Customer updated successfully!');
  };

  const handleAddContact = async (formData) => {
    await contactService.createContact(formData);
    await fetchCustomer();
    setShowContactModal(false);
    showSuccess('Contact added successfully!');
  };

  const handleAddSite = async (formData) => {
    await siteService.createSite(formData);
    await fetchCustomer();
    setShowSiteModal(false);
    showSuccess('Site added successfully!');
  };

  const handleEditSite = async (formData) => {
    await siteService.updateSite(editingSite.id, formData);
    await fetchCustomer();
    setEditingSite(null);
    setShowSiteModal(false);
    showSuccess('Site updated successfully!');
  };

  const handleDeleteSite = async (siteId) => {
    try {
      await siteService.deleteSite(siteId);
      await fetchCustomer();
      setSiteDeleteConfirm(null);
      showSuccess('Site deleted successfully!');
    } catch (err) {
      setError('Failed to delete site.');
    }
  };

  const handleEditJob = async (formData) => {
    const prevStatus = editingJob.status;
    const savedJob = editingJob;
    await jobService.updateJob(savedJob.id, formData);
    setEditingJob(null);
    setShowJobModal(false);
    await fetchCustomer();
    if (prevStatus !== 'Completed' && formData.status === 'Completed') {
      setCompletedJobDialog({ job: savedJob, formData });
    } else {
      showSuccess('Job updated successfully!');
    }
  };

  const handleAddJob = async (formData) => {
    await jobService.createJob(formData);
    await fetchCustomer();
    setShowJobModal(false);
    showSuccess('Job added successfully!');
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await jobService.deleteJob(jobId);
      await fetchCustomer();
      setJobDeleteConfirm(null);
      showSuccess('Job deleted successfully!');
    } catch (err) {
      setError('Failed to delete job.');
    }
  };

  const handleEditContact = async (formData) => {
    await contactService.updateContact(editingContact.id, formData);
    await fetchCustomer();
    setEditingContact(null);
    setShowContactModal(false);
    showSuccess('Contact updated successfully!');
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await contactService.deleteContact(contactId);
      await fetchCustomer();
      setContactDeleteConfirm(null);
      showSuccess('Contact deleted successfully!');
    } catch (err) {
      setError('Failed to delete contact.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-red-500">{error || 'Customer not found.'}</div>
      </div>
    );
  }

  const contacts = customer.contacts || [];
  const sites = customer.sites || [];  const jobs = sites.flatMap(site => (site.jobs || []).map(job => ({ ...job, site })));  const addressParts = [customer.street_address, customer.town, customer.county, customer.postcode, customer.country].filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <Link to="/customers" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Customers</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <span className="text-gray-900 dark:text-white font-medium">{customer?.name || '…'}</span>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <span className="text-sm text-gray-600 dark:text-gray-300">{user?.name}</span>
              <button
                onClick={logout}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {successMessage && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg text-sm">
            ✓ {successMessage}
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Customer header card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{customer.name}</h1>
              {addressParts.length > 0 && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {[customer.street_address, customer.town, customer.county].filter(Boolean).map((line, i) => (
                    <span key={i} className="block">{line}</span>
                  ))}
                  {customer.postcode && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts.join(', '))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block hover:underline text-blue-500 dark:text-blue-400"
                    >
                      {customer.postcode}
                    </a>
                  )}
                  {customer.country && <span className="block text-gray-500 dark:text-gray-400">{customer.country}</span>}
                </div>
              )}
            </div>
            <div className="shrink-0 flex flex-wrap gap-2">
              {addressParts.length > 0 && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts.join(', '))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  Open in Google Maps
                </a>
              )}
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                Edit Customer
              </button>
            </div>
          </div>
        </div>

        {/* Contacts */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Contacts ({contacts.length})
            </h2>
            <button
              onClick={() => { setEditingContact(null); setShowContactModal(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              <span className="text-base leading-none">+</span> Add Contact
            </button>
          </div>
          {contacts.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">No contacts for this customer yet.</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {contacts.map((contact) => (
                <div key={contact.id} className="py-4 flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {contact.first_name} {contact.last_name}
                      {contact.billing_contact && (
                        <span className="ml-2 inline-block px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded-full">
                          Billing
                        </span>
                      )}
                    </p>
                    {contact.position && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{contact.position}</p>
                    )}
                    <div className="flex flex-wrap gap-x-4 mt-1">
                      {contact.email && (
                        <a
                          href={`mailto:${contact.email}`}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {contact.email}
                        </a>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 mt-1">
                      {contact.phone && (
                        <a
                          href={`tel:${contact.phone}`}
                          className="text-sm text-gray-600 dark:text-gray-300 hover:underline"
                        >
                          {contact.phone}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => { setEditingContact(contact); setShowContactModal(true); }}
                      className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setContactDeleteConfirm(contact)}
                      className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sites */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Sites ({sites.length})
            </h2>
            <button
              onClick={() => setShowSiteModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              + Add Site
            </button>
          </div>
          {sites.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">No sites for this customer.</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {sites.map((site) => {
                const siteAddressParts = [site.street_address, site.town, site.county, site.postcode, site.country].filter(Boolean);
                return (
                  <div key={site.id} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <button
                        onClick={() => navigate(`/sites/${site.id}`)}
                        className="font-medium text-blue-600 dark:text-blue-400 hover:underline text-left"
                      >
                        {site.name}
                      </button>
                      {siteAddressParts.length > 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                          {siteAddressParts.join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => { setEditingSite(site); setShowSiteModal(true); }}
                        className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setSiteDeleteConfirm(site)}
                        className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Jobs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Jobs ({jobs.length})
            </h2>
            <button
              onClick={() => setShowJobModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              + Add Job
            </button>
          </div>
          {jobs.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">No jobs for this customer.</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {jobs.map((job) => (
                <div key={job.id} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <button
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      className="font-medium text-blue-600 dark:text-blue-400 hover:underline text-left"
                    >
                      {job.title}
                    </button>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{job.site.name} &middot; {job.status}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => { setEditingJob(job); setShowJobModal(true); }}
                        className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setJobDeleteConfirm(job)}
                        className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors font-medium"
                      >
                        Delete
                      </button>
                    </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Edit Customer Modal */}
      {showEditModal && (
        <CustomerModal
          customer={customer}
          onSave={handleEditCustomer}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* Add / Edit Contact Modal */}
      {showContactModal && (
        <ContactModal
          contact={editingContact}
          defaultCustomerId={!editingContact ? id : undefined}
          onSave={editingContact ? handleEditContact : handleAddContact}
          onClose={() => { setShowContactModal(false); setEditingContact(null); }}
        />
      )}

      {showSiteModal && (
        <SiteModal
          site={editingSite}
          defaultCustomerId={!editingSite ? id : undefined}
          onSave={editingSite ? handleEditSite : handleAddSite}
          onClose={() => { setShowSiteModal(false); setEditingSite(null); }}
        />
      )}

      {completedJobDialog && (
        <JobCompletedDialog
          job={completedJobDialog.job}
          formData={completedJobDialog.formData}
          onClose={(msg) => {
            setCompletedJobDialog(null);
            fetchCustomer();
            if (msg) showSuccess(msg);
          }}
        />
      )}

      {showJobModal && (
        <JobModal
          job={editingJob}
          defaultCustomerId={!editingJob ? id : undefined}
          onSave={editingJob ? handleEditJob : handleAddJob}
          onClose={() => { setShowJobModal(false); setEditingJob(null); }}
        />
      )}

      {/* Delete Job Confirm */}
      {jobDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Job</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete <strong>{jobDeleteConfirm.title}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setJobDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteJob(jobDeleteConfirm.id)}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Site Confirm */}
      {siteDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Site</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete <strong>{siteDeleteConfirm.name}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setSiteDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSite(siteDeleteConfirm.id)}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Contact Confirm */}
      {contactDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Contact</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete{' '}
              <strong>{contactDeleteConfirm.first_name} {contactDeleteConfirm.last_name}</strong>?
              This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setContactDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteContact(contactDeleteConfirm.id)}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewCustomer;
